import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Plus, Pencil, Trash2, Loader2, ImagePlus, Sparkles, X, Save, Upload, Radio } from 'lucide-react';
import {
  listSayings, createSaying, updateSaying, deleteSaying, broadcastSaying,
  uploadFile, resolveFileUrl,
} from '../../services/api';
import { toast } from '../../hooks/use-toast';

const empty = {
  caption: '',
  body: '',
  author: 'Emerald Corporates',
  image_url: '',
  tags: [],
  published_at: '',
  active: true,
};

const SayingsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await listSayings(100)).data || []); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...empty, published_at: new Date().toISOString().slice(0, 10) });
    setDrawerOpen(true);
  };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ ...empty, ...s, published_at: (s.published_at || '').slice(0, 10) });
    setDrawerOpen(true);
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setProgress(0);
    try {
      const uploaded = await uploadFile(file, (p) => setProgress(p));
      setForm((f) => ({ ...f, image_url: uploaded.url || uploaded.file_url }));
      toast({ title: 'Image uploaded' });
    } catch (err) {
      toast({ title: 'Upload failed', variant: 'destructive', description: err?.response?.data?.detail || 'try again' });
    } finally { setUploading(false); setProgress(0); }
  };

  const save = async () => {
    if (!form.image_url) { toast({ title: 'Add an image first', variant: 'destructive' }); return; }
    if (!form.caption?.trim()) { toast({ title: 'Caption is required', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      const payload = { ...form, tags: (form.tags || []).filter(Boolean) };
      if (editing) await updateSaying(editing.id, payload);
      else await createSaying(payload);
      toast({ title: editing ? 'Saying updated' : 'Published — broadcasting to socials' });
      setDrawerOpen(false);
      load();
    } catch (err) {
      toast({ title: 'Save failed', variant: 'destructive', description: err?.response?.data?.detail || 'check the form' });
    } finally { setSaving(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.caption?.slice(0, 40)}…"?`)) return;
    try { await deleteSaying(s.id); load(); }
    catch { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  const broadcast = async (s) => {
    try { await broadcastSaying(s.id); toast({ title: 'Re-broadcast queued', description: 'Zapier will fan out to LinkedIn / X / Facebook.' }); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Broadcast failed', variant: 'destructive' }); }
  };

  return (
    <div data-testid="sayings-page">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
        <div>
          <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">Content</span>
          <h1 className="mt-2 text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>Daily Quotes</h1>
          <p className="mt-2 text-sm text-[#0b1a2e]/60">Image-first micro-posts — quick captions, quotes and AI-generated visuals. Auto-broadcasts to socials on publish.</p>
        </div>
        <button onClick={openCreate} data-testid="new-saying-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#0f5c3d] text-white text-sm font-medium hover:bg-[#0b1a2e] transition">
          <Plus className="w-4 h-4" /> New Saying
        </button>
      </div>

      {loading ? (
        <div className="bg-white border border-black/5 rounded-2xl p-10 text-center text-[#0b1a2e]/60">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-black/5 rounded-2xl p-10 text-center">
          <Sparkles className="w-10 h-10 mx-auto text-[#0b1a2e]/30 mb-3" />
          <p className="text-[#0b1a2e]/60 mb-4">No sayings yet. Upload a Genora image + a one-liner to get started.</p>
          <button onClick={openCreate} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0f5c3d] text-white text-sm">
            <Plus className="w-4 h-4" /> Publish first saying
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((s) => (
            <div key={s.id} className="bg-white border border-black/5 rounded-2xl overflow-hidden hover:shadow-md transition" data-testid={`saying-card-${s.id}`}>
              <div className="aspect-square bg-[#e2ede6] overflow-hidden">
                {s.image_url ? <img src={resolveFileUrl(s.image_url)} alt={s.caption} className="w-full h-full object-cover" /> :
                  <div className="flex items-center justify-center h-full text-[#0b1a2e]/30"><ImagePlus className="w-8 h-8" /></div>}
              </div>
              <div className="p-4">
                <div className="text-xs text-[#0b1a2e]/60">{(s.published_at || s.created_at || '').slice(0, 10)}{s.author ? ` · ${s.author}` : ''}</div>
                <p className="mt-1 text-sm text-[#0b1a2e] line-clamp-3 leading-relaxed">{s.caption}</p>
                {!s.active && <span className="mt-2 inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 uppercase tracking-widest">Draft</span>}
                <div className="mt-3 flex gap-2">
                  <button onClick={() => openEdit(s)} className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg border border-[#0f5c3d]/20 text-xs font-medium hover:bg-[#f7fbf8]"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                  <button onClick={() => broadcast(s)} title="Re-broadcast to socials" data-testid={`saying-broadcast-${s.id}`}
                    className="inline-flex items-center justify-center px-3 py-2 rounded-lg text-[#0f5c3d] hover:bg-[#f7fbf8]"><Radio className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(s)} className="inline-flex items-center justify-center px-3 py-2 rounded-lg text-red-600 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {drawerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="saying-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDrawerOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col">
            <div className="px-5 py-4 bg-[#0b1a2e] text-white flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#7bbf95] font-semibold">Daily Saying</div>
                <div className="font-medium">{editing ? 'Edit' : 'New'} Saying</div>
              </div>
              <button onClick={() => setDrawerOpen(false)}><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto">
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-[#0b1a2e]/60 mb-1 font-semibold">Image *</label>
                {form.image_url ? (
                  <div className="relative aspect-square rounded-lg overflow-hidden bg-[#f7fbf8]">
                    <img src={resolveFileUrl(form.image_url)} alt="preview" className="w-full h-full object-cover" />
                    <button onClick={() => setForm({ ...form, image_url: '' })} className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 text-white hover:bg-black"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ) : (
                  <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="saying-image-upload"
                    className="w-full aspect-square rounded-lg border-2 border-dashed border-[#0f5c3d]/25 flex flex-col items-center justify-center gap-2 text-[#0f5c3d] hover:bg-[#f7fbf8]">
                    {uploading ? <><Loader2 className="w-5 h-5 animate-spin" /><span className="text-xs">Uploading… {progress}%</span></> :
                      <><Upload className="w-6 h-6" /><span className="text-xs font-medium">Upload from Genora</span></>}
                  </button>
                )}
                <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" />
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-widest text-[#0b1a2e]/60 mb-1 font-semibold">Caption / Quote *</label>
                <textarea rows={3} value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })}
                  data-testid="saying-caption"
                  placeholder="e.g. Success is where preparation and opportunity meet."
                  className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
                <div className="text-[10px] text-[#0b1a2e]/50 mt-1">{form.caption.length}/500 · this becomes the social-post text</div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#0b1a2e]/60 mb-1 font-semibold">Author</label>
                  <input value={form.author || ''} onChange={(e) => setForm({ ...form, author: e.target.value })} placeholder="Emerald Corporates" className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#0b1a2e]/60 mb-1 font-semibold">Publish date</label>
                  <input type="date" value={(form.published_at || '').slice(0, 10)} onChange={(e) => setForm({ ...form, published_at: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
                </div>
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-widest text-[#0b1a2e]/60 mb-1 font-semibold">Long text (optional)</label>
                <textarea rows={3} value={form.body || ''} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Add context or a longer reflection (shown only on the detail page)" className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#0f5c3d]" data-testid="saying-active" />
                Active — publish + broadcast to socials
              </label>
            </div>
            <div className="px-5 py-3 border-t border-black/5 bg-[#f7fbf8] flex justify-end gap-2">
              <button onClick={() => setDrawerOpen(false)} className="px-4 py-2 rounded-lg text-sm hover:bg-black/5">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="saving-save"
                className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#0f5c3d] text-white text-sm font-semibold disabled:opacity-60 hover:bg-[#0b4a30]">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {editing ? 'Save' : 'Publish'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SayingsPage;
